import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UUID } from "../utils";
import listFactory from "./listFactory";
import { RootState } from "./index";
import { CustomerPaymentProvider } from "../models/CustomerPaymentProvider";
import { find, findIndex } from "lodash";

export interface SetCustomerPaymentsProviderActiveFlagProps {
  customerId: UUID;
  providerId: UUID;
  active: boolean;
}

const { slicePart, selectors } = listFactory<CustomerPaymentProvider>(
  "customersPaymentsProviders"
);

const initialState = {
  ...slicePart.initialState,
};

export const slice = createSlice({
  ...slicePart,
  initialState,
  reducers: {
    setList: slicePart.reducers.setList,
    setItem: (
      state,
      action: PayloadAction<{
        customerId: UUID;
        providerId: UUID;
        item: CustomerPaymentProvider;
      }>
    ) => {
      const { customerId, providerId, item: itemRaw } = action.payload;

      const itemExists = !!find(state.list, {
        customerId,
        provider: { id: providerId },
      });

      let item = { ...itemRaw, customerId };

      if (itemExists) {
        const index = findIndex(state.list, {
          customerId,
          provider: { id: providerId },
        });

        state.list.splice(index, 1, item);
      } else {
        state.list.unshift(item);
      }
    },
    updateItem: (
      state,
      action: PayloadAction<{
        customerId: UUID;
        providerId: UUID;
        itemPart: Partial<CustomerPaymentProvider>;
      }>
    ) => {
      const { customerId, providerId, itemPart } = action.payload;
      const item = find(state.list, {
        customerId,
        provider: { id: providerId },
      });
      Object.assign(item, itemPart);
    },
  },
});

export const {
  setList: setCustomerPaymentsProviders,
  setItem: setCustomerPaymentsProvider,
  updateItem: updateCustomerPaymentsProviderPart,
} = slice.actions;

export const fetchCustomerPaymentsProviders = createAction<{
  customerId: UUID;
}>("fetchCustomerPaymentsProviders");

export const updateCustomerPaymentsProvider = createAction<{
  customerId: UUID;
  providerId: UUID;
  data: CustomerPaymentProvider;
}>("updateCustomerPaymentsProvider");

export const setCustomerPaymentsProviderActiveFlag = createAction<SetCustomerPaymentsProviderActiveFlagProps>(
  "setCustomerPaymentsProviderActiveFlag"
);

export const selectCustomerPaymentsProviders = (customerId: UUID) => (
  state: RootState
) => {
  return state.customersPaymentsProviders.list.filter(
    (config) => config.customerId === customerId
  );
};

export const selectCustomerPaymentsProvider = (
  customerId: UUID,
  providerId: UUID
) => (state: RootState): CustomerPaymentProvider | undefined => {
  return state.customersPaymentsProviders.list.find(
    (config) =>
      config.customerId === customerId && config.provider.id === providerId
  );
};

export const { selectList: selectCashManagementProviders } = selectors;

export const customemersPaymentsProvidersOperationPaths = ({
  customerId,
  providerId,
}: {
  customerId: UUID;
  providerId?: UUID;
}) => {
  return {
    listing: ["customersPaymentsProviders", customerId, "list"],
    update: ["customersPaymentsProviders", customerId, providerId, "update"],
    setActiveFlag: [
      "customersPaymentsProviders",
      customerId,
      providerId,
      "setActiveFlag",
    ],
  };
};

export default slice.reducer;
